// Per-card session state machine + priority queue for the N5 territory.
//
// State:
//   fresh      — never reviewed this session
//   unknown    — last review was Unknown
//   known1     — one Known mark, no consecutive run yet
//   graduated  — two consecutive Knowns this session; locked, removed from queue
//
// Queue (recomputed after every review):
//   1. fresh    (corpus frequency desc)
//   2. unknown  (lastReviewedAt desc — most recently failed first)
//   3. known1   (lastReviewedAt desc)
//   graduated cards are excluded.
//
// localStorage persistence is keyed by batchId so dismissing a card,
// returning to the map, and tapping any tile resumes the queue.

import { useCallback, useEffect, useRef, useState } from "react";
import type { TreemapEntry } from "@bindery/yominoki-db/queries/treemap";

export type CardState = "fresh" | "unknown" | "known1" | "graduated";

export interface CardSession {
  /** jlpt_vocab identity triple — matches the PK. */
  jmdict_seq: string;
  kana: string;
  kanji: string;
  state: CardState;
  consecutiveKnowns: number; // 0..2
  lastReviewedAt: number; // 0 if never reviewed
}

export interface SessionState {
  byKey: Record<string, CardSession>;
  /** Card key currently held at the front of the queue (sticky head). */
  head: string | null;
}

export type ReviewMark = "known" | "unknown";

const STORAGE_PREFIX = "n5-session";

export function cardKey(c: {
  jmdict_seq: string;
  kana: string;
  kanji: string;
}): string {
  return `${c.jmdict_seq}::${c.kana}::${c.kanji}`;
}

function freshState(entries: readonly TreemapEntry[]): SessionState {
  const byKey: Record<string, CardSession> = {};
  for (const e of entries) {
    byKey[cardKey(e)] = {
      jmdict_seq: e.jmdict_seq,
      kana: e.kana,
      kanji: e.kanji,
      state: "fresh",
      consecutiveKnowns: 0,
      lastReviewedAt: 0,
    };
  }
  return { byKey, head: null };
}

function load(batchId: string, entries: readonly TreemapEntry[]): SessionState {
  if (typeof window === "undefined") return freshState(entries);
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}:${batchId}`);
    if (!raw) return freshState(entries);
    const parsed = JSON.parse(raw) as SessionState;
    // Heal: ensure every entry in the current batch has a slot. (Unlikely
    // to mismatch in practice, but cheap defense.)
    const fresh = freshState(entries);
    for (const k of Object.keys(fresh.byKey)) {
      parsed.byKey[k] ??= fresh.byKey[k]!;
    }
    return parsed;
  } catch {
    return freshState(entries);
  }
}

function persist(batchId: string, state: SessionState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${STORAGE_PREFIX}:${batchId}`,
      JSON.stringify(state),
    );
  } catch {
    // Quota / disabled storage — silently ignore in prototype.
  }
}

/** Compute the priority queue: fresh (frequency desc) → unknown → known1. */
export function buildQueue(
  state: SessionState,
  entries: readonly TreemapEntry[],
): string[] {
  const fresh: string[] = [];
  const unknown: { key: string; t: number }[] = [];
  const known1: { key: string; t: number }[] = [];

  // entries is already frequency desc (composeBatch sorts on output).
  for (const e of entries) {
    const k = cardKey(e);
    const cs = state.byKey[k];
    if (!cs) continue;
    if (cs.state === "fresh") fresh.push(k);
    else if (cs.state === "unknown")
      unknown.push({ key: k, t: cs.lastReviewedAt });
    else if (cs.state === "known1")
      known1.push({ key: k, t: cs.lastReviewedAt });
  }
  unknown.sort((a, b) => b.t - a.t);
  known1.sort((a, b) => b.t - a.t);
  const ordered = [
    ...fresh,
    ...unknown.map((x) => x.key),
    ...known1.map((x) => x.key),
  ];

  if (state.head && ordered.includes(state.head)) {
    return [state.head, ...ordered.filter((k) => k !== state.head)];
  }
  return ordered;
}

export function reduce(
  state: SessionState,
  key: string,
  mark: ReviewMark,
  now: number,
): { next: SessionState; leveledUp: boolean; graduated: boolean } {
  const cur = state.byKey[key];
  if (!cur) return { next: state, leveledUp: false, graduated: false };
  if (cur.state === "graduated") {
    return { next: state, leveledUp: false, graduated: false };
  }

  let nextState: CardState;
  let consecutiveKnowns: number;
  let leveledUp = false;
  let graduated = false;

  if (mark === "known") {
    if (cur.state === "known1") {
      nextState = "graduated";
      consecutiveKnowns = 2;
      leveledUp = true;
      graduated = true;
    } else {
      nextState = "known1";
      consecutiveKnowns = 1;
      leveledUp = true;
    }
  } else {
    nextState = "unknown";
    consecutiveKnowns = 0;
  }

  const updated: CardSession = {
    ...cur,
    state: nextState,
    consecutiveKnowns,
    lastReviewedAt: now,
  };
  return {
    next: { ...state, byKey: { ...state.byKey, [key]: updated }, head: null },
    leveledUp,
    graduated,
  };
}

export function fillRatio(state: SessionState): number {
  const keys = Object.keys(state.byKey);
  if (keys.length === 0) return 0;
  let g = 0;
  for (const k of keys) if (state.byKey[k]!.state === "graduated") g++;
  return g / keys.length;
}

export function levelPct(s: CardState): number {
  switch (s) {
    case "fresh":
    case "unknown":
      return 0;
    case "known1":
      return 50;
    case "graduated":
      return 100;
  }
}

export interface UseSessionReturn {
  state: SessionState;
  queue: string[];
  fillRatio: number;
  review(
    key: string,
    mark: ReviewMark,
  ): { leveledUp: boolean; graduated: boolean };
  setHead(key: string | null): void;
  reset(): void;
}

export function useSessionState(
  batchId: string,
  entries: readonly TreemapEntry[],
): UseSessionReturn {
  // Hydrate from localStorage on mount; the SSR pass uses fresh state so
  // server and first client paint match.
  const [state, setState] = useState<SessionState>(() => freshState(entries));
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    // setState in an effect on purpose: one-shot hydration from localStorage (an external system); the first render used freshState so the first paint matches.
    setState(load(batchId, entries));
  }, [batchId, entries]);

  useEffect(() => {
    if (!hydrated.current) return;
    persist(batchId, state);
  }, [batchId, state]);

  // Mirror state in a ref so review() can derive its result synchronously
  // from the pre-update value. The previous version mutated closed-over
  // let vars inside the setState updater, which corrupts under strict-mode
  // double-invocation (the updater runs twice; second run overwrites the
  // first run's flags).
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const review = useCallback((key: string, mark: ReviewMark) => {
    const r = reduce(stateRef.current, key, mark, Date.now());
    setState(r.next);
    return { leveledUp: r.leveledUp, graduated: r.graduated };
  }, []);

  const setHead = useCallback((key: string | null) => {
    setState((cur) => (cur.head === key ? cur : { ...cur, head: key }));
  }, []);

  const reset = useCallback(() => {
    setState(freshState(entries));
  }, [entries]);

  const queue = buildQueue(state, entries);
  return {
    state,
    queue,
    fillRatio: fillRatio(state),
    review,
    setHead,
    reset,
  };
}
